import React, { useEffect, useState } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Row,
  Table,
  Button,
  Input,
  Badge,
  Spinner,
  Collapse,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { workTypesAPI } from '../../helpers/api';
import { useIsMobile } from '../../helpers/useMediaQuery';
import WorkTypeFormModal from './WorkTypeFormModal';
import WorkTypeDetailModal from './WorkTypeDetailModal';

interface WorkType {
  id: string;
  name: string;
  unit: string;
  description?: string;
  isActive: boolean;
  createdAt: string;
}

const WorkTypes: React.FC = () => {
  const isMobile = useIsMobile();
  const [workTypes, setWorkTypes] = useState<WorkType[]>([]);
  const [loading, setLoading] = useState(true);

  // Form modal state (for new work type)
  const [formModalOpen, setFormModalOpen] = useState(false);

  // Detail modal state
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedWorkType, setSelectedWorkType] = useState<WorkType | null>(null);

  // Filter states
  const [statusFilter, setStatusFilter] = useState('');
  const [unitFilter, setUnitFilter] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Units for filter dropdown
  const [units, setUnits] = useState<string[]>([]);

  useEffect(() => {
    fetchUnits();
  }, []);

  useEffect(() => {
    fetchWorkTypes();
  }, [statusFilter, unitFilter, searchFilter]);

  const fetchWorkTypes = async () => {
    try {
      setLoading(true);
      const params: { isActive?: boolean } = {};
      if (statusFilter !== '') params.isActive = statusFilter === 'true';

      const response = await workTypesAPI.getAll(params);
      let data = response.data as WorkType[];

      // Client-side unit filter (backend doesn't have unit filter)
      if (unitFilter) {
        data = data.filter(wt => wt.unit === unitFilter);
      }

      if (searchFilter) {
        const s = searchFilter.toLowerCase();
        data = data.filter(wt =>
          wt.name.toLowerCase().includes(s) ||
          wt.unit.toLowerCase().includes(s) ||
          (wt.description && wt.description.toLowerCase().includes(s))
        );
      }

      setWorkTypes(data);
    } catch (error) {
      console.error('Work types fetch error:', error);
      toast.error('İş türleri yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const fetchUnits = async () => {
    try {
      const response = await workTypesAPI.getUnits();
      setUnits(response.data);
    } catch (error) {
      console.error('Units fetch error:', error);
    }
  };

  const openDetailModal = (workType: WorkType, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setSelectedWorkType(workType);
    setDetailModalOpen(true);
  };

  const closeDetailModal = () => {
    setDetailModalOpen(false);
    setSelectedWorkType(null);
  };

  const clearFilters = () => {
    setStatusFilter('');
    setUnitFilter('');
    setSearchFilter('');
  };

  const hasActiveFilters = statusFilter !== '' || unitFilter !== '' || searchFilter !== '';

  return (
    <>
      <Row>
        <Col xs={12}>
          <div className="page-title-box d-sm-flex align-items-center justify-content-between">
            <h4 className="mb-sm-0">İş Türleri</h4>
            <div className="page-title-right">
              <Button color="primary" onClick={() => setFormModalOpen(true)}>
                <i className="ri-add-line me-1"></i>
                Yeni İş Türü
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      <Row>
        <Col lg={12}>
          <Card>
            <CardHeader>
              <Row className="align-items-center g-3">
                <Col md={3}>
                  <h5 className="card-title mb-0">İş Türleri Listesi</h5>
                </Col>
                {/* Desktop Filters */}
                {!isMobile && (
                  <Col md={9}>
                    <Row className="g-2 align-items-center justify-content-end">
                      <Col md="auto">
                        <Input
                          type="text"
                          bsSize="sm"
                          value={searchFilter}
                          onChange={(e) => setSearchFilter(e.target.value)}
                          placeholder="İş adı, birim ara..."
                          style={{ width: '170px' }}
                        />
                      </Col>
                      <Col md="auto">
                        <Input
                          type="select"
                          bsSize="sm"
                          value={unitFilter}
                          onChange={(e) => setUnitFilter(e.target.value)}
                          style={{ minWidth: '120px' }}
                        >
                          <option value="">Tüm Birimler</option>
                          {units.map((unit) => (
                            <option key={unit} value={unit}>{unit}</option>
                          ))}
                        </Input>
                      </Col>
                      <Col md="auto">
                        <Input
                          type="select"
                          bsSize="sm"
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                          style={{ minWidth: '120px' }}
                        >
                          <option value="">Tüm Durumlar</option>
                          <option value="true">Aktif</option>
                          <option value="false">Pasif</option>
                        </Input>
                      </Col>
                      {hasActiveFilters && (
                        <Col md="auto">
                          <Button
                            color="light"
                            size="sm"
                            onClick={clearFilters}
                            title="Filtreleri Temizle"
                          >
                            <i className="ri-close-line"></i>
                          </Button>
                        </Col>
                      )}
                    </Row>
                  </Col>
                )}
              </Row>
            </CardHeader>
            <CardBody>
              {/* Mobile Filters */}
              {isMobile && (
                <>
                  <button
                    className="btn btn-sm btn-light w-100 mb-3 d-flex align-items-center justify-content-between"
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    <span>
                      <i className="ri-filter-3-line me-2"></i>
                      Filtreler
                      {hasActiveFilters && (
                        <Badge color="primary" className="ms-2">
                          {[statusFilter, unitFilter].filter(Boolean).length}
                        </Badge>
                      )}
                    </span>
                    <i className={`ri-arrow-${showFilters ? 'up' : 'down'}-s-line`}></i>
                  </button>

                  <Collapse isOpen={showFilters}>
                    <div className="bg-light p-3 rounded mb-3">
                      <div className="mb-2">
                        <label className="small text-muted mb-1">Ara</label>
                        <Input
                          type="text"
                          bsSize="sm"
                          value={searchFilter}
                          onChange={(e) => setSearchFilter(e.target.value)}
                          placeholder="İş adı, birim ara..."
                        />
                      </div>

                      <div className="mb-2">
                        <label className="small text-muted mb-1">Birim</label>
                        <Input
                          type="select"
                          bsSize="sm"
                          value={unitFilter}
                          onChange={(e) => setUnitFilter(e.target.value)}
                        >
                          <option value="">Tüm Birimler</option>
                          {units.map((unit) => (
                            <option key={unit} value={unit}>{unit}</option>
                          ))}
                        </Input>
                      </div>

                      <div className="mb-2">
                        <label className="small text-muted mb-1">Durum</label>
                        <Input
                          type="select"
                          bsSize="sm"
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                        >
                          <option value="">Tüm Durumlar</option>
                          <option value="true">Aktif</option>
                          <option value="false">Pasif</option>
                        </Input>
                      </div>

                      {hasActiveFilters && (
                        <button
                          className="btn btn-sm btn-outline-secondary w-100 mt-2"
                          onClick={clearFilters}
                        >
                          <i className="ri-close-line me-1"></i>
                          Filtreleri Temizle
                        </button>
                      )}
                    </div>
                  </Collapse>
                </>
              )}

              {loading ? (
                <div className="text-center py-5">
                  <Spinner color="primary" />
                  <p className="mt-2 text-muted">Yükleniyor...</p>
                </div>
              ) : workTypes.length === 0 ? (
                <div className="text-center py-5">
                  <i className="ri-file-list-3-line display-4 text-muted"></i>
                  <p className="mt-3 text-muted">Henüz iş türü tanımlanmamış</p>
                  <Button color="primary" size="sm" onClick={() => setFormModalOpen(true)}>
                    İlk İş Türünü Ekle
                  </Button>
                </div>
              ) : isMobile ? (
                // Mobile: Card View
                <div className="admin-card-list">
                  {workTypes.map((wt) => (
                    <div key={wt.id} className="admin-card-item">
                      {/* Card Header */}
                      <div
                        className="admin-card-header"
                        onClick={() => openDetailModal(wt)}
                        style={{ cursor: 'pointer' }}
                      >
                        <h6 className="admin-card-title">{wt.name}</h6>
                        <span
                          className="px-2 py-1 rounded-pill small bg-info bg-opacity-10 text-info"
                          style={{ fontWeight: 500 }}
                        >
                          {wt.unit}
                        </span>
                      </div>

                      {/* Card Body */}
                      <div
                        className="admin-card-body"
                        onClick={() => openDetailModal(wt)}
                        style={{ cursor: 'pointer' }}
                      >
                        {wt.description && (
                          <div className="admin-info-row">
                            <span className="admin-info-label">
                              <i className="ri-file-text-line"></i>
                              Açıklama
                            </span>
                            <span className="admin-info-value">{wt.description}</span>
                          </div>
                        )}
                        <div className="admin-info-row">
                          <span className="admin-info-label">
                            <i className="ri-toggle-line"></i>
                            Durum
                          </span>
                          <span className="admin-info-value">
                            <span
                              className={`px-2 py-1 rounded-pill small ${
                                wt.isActive
                                  ? 'bg-success bg-opacity-10 text-success'
                                  : 'bg-secondary bg-opacity-10 text-secondary'
                              }`}
                              style={{ fontWeight: 500 }}
                            >
                              {wt.isActive ? 'Aktif' : 'Pasif'}
                            </span>
                          </span>
                        </div>
                      </div>

                      {/* Card Footer */}
                      <div className="admin-card-footer">
                        <button
                          type="button"
                          className="admin-action-btn admin-btn-primary"
                          style={{ flex: 1 }}
                          onClick={(e) => openDetailModal(wt, e)}
                        >
                          <i className="ri-eye-line"></i>
                          Detaylar
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                // Desktop: Table View
                <div className="table-responsive">
                  <Table className="table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>İş Adı</th>
                        <th>Birim</th>
                        <th>Açıklama</th>
                        <th>Durum</th>
                      </tr>
                    </thead>
                    <tbody>
                      {workTypes.map((wt) => (
                        <tr
                          key={wt.id}
                          onClick={() => openDetailModal(wt)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td>
                            <strong>{wt.name}</strong>
                          </td>
                          <td>
                            <span
                              className="px-2 py-1 rounded-pill small bg-info bg-opacity-10 text-info"
                              style={{ fontWeight: 500 }}
                            >
                              {wt.unit}
                            </span>
                          </td>
                          <td className="text-muted">
                            {wt.description || '-'}
                          </td>
                          <td>
                            <span
                              className={`px-2 py-1 rounded-pill small ${
                                wt.isActive
                                  ? 'bg-success bg-opacity-10 text-success'
                                  : 'bg-secondary bg-opacity-10 text-secondary'
                              }`}
                              style={{ fontWeight: 500 }}
                            >
                              {wt.isActive ? 'Aktif' : 'Pasif'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Yeni İş Türü Modal */}
      <WorkTypeFormModal
        isOpen={formModalOpen}
        toggle={() => setFormModalOpen(false)}
        onSuccess={() => {
          fetchWorkTypes();
          fetchUnits();
        }}
      />

      {/* İş Türü Detay Modal */}
      <WorkTypeDetailModal
        isOpen={detailModalOpen}
        toggle={closeDetailModal}
        workType={selectedWorkType}
        onUpdate={() => {
          fetchWorkTypes();
          fetchUnits();
        }}
      />
    </>
  );
};

export default WorkTypes;
